import type { Dispatch } from 'redux';
import type { ReduxAction } from './types';
import type { Color } from '../types/Color';
import { createAction } from './utils/createAction.ts';
import { createReducerBuilder } from './utils/createReducerBuilder.ts';
import { createSelector } from './utils/createSelector.ts';
import { removeValueMatching } from '../utils/immutable/array/removeValueMatching.ts';
import { replaceValue } from '../utils/immutable/array/replaceValue.ts';
import { replaceValueMatching } from '../utils/immutable/array/replaceValueMatching.ts';
import { randomString } from '../utils/string/randomString.ts';

// Should match the transition time(s) of <toasts-container> CSS
export const TOAST_FADE_IN_DURATION = Math.floor(1000 * .33);
export const TOAST_FADE_OUT_DURATION = Math.floor(1000 * .33);

export const ToastState = {
  FadeIn: 'fade-in',
  Static: 'static',
  FadeOut: 'fade-out'
} as const;

export type ToastState = ValueOf<typeof ToastState>;

export const ToastActionType = {
  Add: 'Add',
  Update: 'Update',
  Clear: 'Clear'
} as const;

export type ToastActionType = ValueOf<typeof ToastActionType>;

interface Options {
  /** The ID of the toast. */
  id?: string;

  /** Color hex-string */
  color?: Color;

  /** Time before the toast is removed, in ms. */
  timeout?: number;
}

interface ToastProperties extends Options {
  /** The toast message */
  message?: string;

  state?: ToastState;
}

export type Toast = ReduxAction & ToastProperties;

export interface ToastReduxState {
  toasts?: ToastProperties[];
}

export const { toasts, addToast, clearToast, toastSelector } = createReducerBuilder<ToastActionType, 'toasts', ToastReduxState>('toasts')
  .addAction('addToast', createAction(
    (message: string, { color = '#27AE60', timeout = 5000, id = randomString(24) }: Options = {}) =>
      (dispatch: Dispatch<Toast & { type: string }>) => {
        requestAnimationFrame(() => {
          dispatch({
            type: ToastActionType.Update,
            id,
            state: ToastState.Static
          });
        });

        setTimeout(() => {
          dispatch({
            type: ToastActionType.Update,
            id,
            state: ToastState.FadeOut
          });
        }, TOAST_FADE_IN_DURATION + timeout);

        setTimeout(() => {
          dispatch({
            type: ToastActionType.Clear,
            id
          });
        }, timeout + TOAST_FADE_IN_DURATION + TOAST_FADE_OUT_DURATION);

        dispatch({
          type: ToastActionType.Add,
          id,
          message,
          color,
          state: ToastState.FadeIn
        });
      }
  ))
  .addAction('clearToast', createAction(
    (id: string) =>
      (dispatch: Dispatch<Toast & { type: string }>) => {
        dispatch({
          type: ToastActionType.Clear,
          id
        })
      }
  ))
  .addReducer(ToastActionType.Add, (state: ToastReduxState, { message, color, id, state: toastState }: Toast): ToastReduxState => ({
    ...state,
    toasts: replaceValueMatching(
      state.toasts ?? [],
      toast => toast.id === id,
      { message, color, id, state: toastState }
    )
  }))
  .addReducer(ToastActionType.Update, (state: ToastReduxState, { id, state: toastState }: Toast): ToastReduxState => {
    const toast = state.toasts?.find((toast: ToastProperties) => toast.id === id);

    return ({
      ...state,
      toasts: toast
        ? replaceValue(state.toasts ?? [], toast, { ...toast, state: toastState })
        : []
    });
  })
  .addReducer(ToastActionType.Clear, (state, { id }: Toast) => ({
    ...state,
    toasts: removeValueMatching(state.toasts ?? [], (toast: ToastProperties) => toast.id === id)
  }))
  .addSelector('toastSelector', createSelector<Toast[]>('toasts.toasts', []))
  .build();